import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { clearSslPurchase, pendingSslPurchase, safeDashboardReturn } from '../lib/authReturn';

interface AuthContextType {
  tenantId: string | null;
  isAdmin: boolean;
  login: (tenantId: string, isAdmin: boolean) => void;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tenantId, setTenantId] = useState<string | null>(localStorage.getItem('tenantId'));
  const [isAdmin, setIsAdmin] = useState<boolean>(localStorage.getItem('isAdmin') === 'true');
  const navigate = useNavigate();

  const login = (newTenantId: string, admin: boolean) => {
    sessionStorage.removeItem('activeWorkspaceId');
    setTenantId(newTenantId);
    setIsAdmin(admin);
    localStorage.setItem('tenantId', newTenantId);
    localStorage.setItem('isAdmin', admin ? 'true' : 'false');
    const returnTo = new URLSearchParams(window.location.search).get('returnTo');
    const destination = safeDashboardReturn(returnTo) ?? (returnTo ? null : pendingSslPurchase()) ?? '/dashboard';
    clearSslPurchase();
    navigate(destination);
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      // Ignore network errors on logout
    }
    setTenantId(null);
    setIsAdmin(false);
    localStorage.removeItem('tenantId');
    localStorage.removeItem('isAdmin');
    sessionStorage.removeItem('activeWorkspaceId');
    clearSslPurchase();
    // Use hard navigation to avoid ProtectedRoute redirect race to /login
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{ tenantId, isAdmin, login, logout, isAuthenticated: !!tenantId }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  useEffect(() => {
    if (!isAuthenticated) {
      window.location.href = `/login?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`;
    }
  }, [isAuthenticated]);

  return isAuthenticated ? <>{children}</> : null;
};

export const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isAdmin } = useAuth();
  
  useEffect(() => {
    if (!isAuthenticated) {
      window.location.href = '/login';
    } else if (!isAdmin) {
      window.location.href = '/dashboard';
    }
  }, [isAuthenticated, isAdmin]);

  return isAuthenticated && isAdmin ? <>{children}</> : null;
};
